import type * as AdyenWeb from '@adyen/adyen-web';
import type { Dropin, ICore, PaymentAction } from '@adyen/adyen-web';
import { useEffect, useState } from 'react';

import { appendScript } from '../ts/append-script';
import { appendStylesheet } from '../ts/append-stylesheet';

export type ApiResponse = {
  action?: PaymentAction;
  resultCode: string;
  pspReference?: string;
};

export type AdyenState = {
  data: {
    billingAddress: unknown;
    browserInfo: unknown;
    paymentMethod: Record<string, unknown>;
  };
  [key: string]: unknown;
};

export type Core = ICore;
export type DropinElement = Dropin;

declare global {
  interface Window {
    AdyenWeb?: typeof AdyenWeb;
  }
}

/**
 * https://docs.adyen.com/online-payments/release-notes/?integration_type=web
 */
// NOTE: When upgrading Adyen, the CDN link version needs to be updated and the integrity hash also. Note that Adyen's release notes might lag behind the recommended NPM version. Upgrade to latest NPM version with release notes to see the hash.
export const useAdyen = (
  onError: () => void
): [isLoading: boolean, hasLoaded: boolean] => {
  const [state, setState] = useState({ isLoading: false, hasLoaded: false });

  useEffect(() => {
    if (window.AdyenWeb) {
      setState({
        isLoading: false,
        hasLoaded: true,
      });
      return;
    }

    setState({
      isLoading: true,
      hasLoaded: false,
    });

    appendStylesheet(
      'https://checkoutshopper-test.cdn.adyen.com/checkoutshopper/sdk/6.18.0/adyen.css',
      {
        crossorigin: 'anonymous',
        integrity:
          'sha384-lCDmpxn4G68y4vohxVVEuRcbz4iZTDh1u/FLlsCV1wSbibWKs+knhLQpgzPBqap4',
      }
    );

    appendScript(
      'https://checkoutshopper-test.cdn.adyen.com/checkoutshopper/sdk/6.18.0/adyen.js',
      () => {
        setState({
          isLoading: false,
          hasLoaded: Boolean(window.AdyenWeb),
        });
      },
      {
        crossorigin: 'anonymous',
        integrity:
          'sha384-ZEPvk8M++Rrf/1zMUvnfdO73cZlnj/u9oAGHSeUIIgOXoW0ZrwfyB6pBcIrhDbdd',
        onError: () => {
          onError();
          setState({
            isLoading: false,
            hasLoaded: Boolean(window.AdyenWeb),
          });
        },
      }
    );
  }, []);

  return [state.isLoading, state.hasLoaded];
};
